import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import {
  injectAdTag,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { computed, ComputedRef } from "vue";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import { Offer } from "@fresh8/types/src/betData";
import { injectMarketCards } from "../composition/card-creation";

export function useShouldDisplayOffer(
  betData = injectBetData(),
  brand = injectBrand(),
  adTag = injectAdTag(),
  previewContext = injectPreview()
): ComputedRef<boolean> {
  const displayOfferCard = computed<boolean>(
    () => !!brand?.adTemplate?.value?.cardOrdering?.general?.includes("offer")
  );

  const displayOffer = computed(() => {
    // If it's experiments preview we show offer
    if (
      previewContext?.preview.value.isPreview &&
      previewContext?.preview.value.isExperiment
    ) {
      return true;
    }

    // Allow the preview to turn off the offer
    if (
      previewContext?.preview.value.isPreview &&
      previewContext?.preview.value.hideOffer
    ) {
      return false;
    }

    // Override for previewing the offer for an operator on brands page.
    if (
      previewContext?.preview.value.useBrandOffer &&
      adTag?.operator.value &&
      brand?.offers.value?.[adTag.selectedOperator.value?._id || ""]
    ) {
      return true;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return true;
      // Check to see if we can use the brand based offer for the opertor
    } else {
      // Has an offer been defined for this opertor

      if (
        adTag &&
        adTag.selectedOperator.value &&
        brand?.operatorHasOffer(adTag.selectedOperator.value?._id || "")
      ) {
        // Are offers enabled for this brand and is there bet data available
        if (
          displayOfferCard.value &&
          Object.keys(betData?.markets?.value || {}).length > 0
        ) {
          return true;
        }

        // Are there no markets, there is an offer for this operator and fallback offers are enabled
        if (
          displayOfferCard.value &&
          Object.keys(betData?.markets?.value || {}).length === 0 &&
          brand?.adTemplate.value?.displayOffersAsFallback &&
          brand?.operatorHasOffer(adTag.selectedOperator.value?._id || "")
        ) {
          return true;
        }
        // if offers are disabled
        return false;
      }
    }
    return false;
  });

  return displayOffer;
}

export function useOfferCardDisplayState(): {
  offerDisplayedAsFallback: ComputedRef<boolean>;
  defaultOfferDisplayedAsFirstCard: ComputedRef<boolean>;
} {
  const cards = injectMarketCards().cards;
  const betData = injectBetData();
  const brand = injectBrand();
  const adTag = injectAdTag();
  /**
   * This checks to see if the offer was displayed as a fallback or not
   * used for the anayltics reporting
   */
  const offerDisplayedAsFallback = computed(() => {
    // Has an offer been defiend for this opertor
    if (
      adTag &&
      adTag.selectedOperator.value &&
      brand?.operatorHasOffer(adTag.selectedOperator.value?._id || "")
    ) {
      // Are there no markets and should there a fallback offer be shown
      if (
        cards.value.length === 1 &&
        cards.value[0].type === "offer" &&
        brand?.adTemplate.value?.displayOffersAsFallback
      ) {
        return true;
      }
    }

    return false;
  });

  /**
   * This checks to see if the default offer was displayed.
   * used for the anayltics reporting
   */
  const defaultOfferDisplayedAsFirstCard = computed(() => {
    const displayOfferCard = computed<boolean>(
      () =>
        (brand?.adTemplate.value?.eventCardOrder || []).indexOf("offer") > -1
    );
    // If there's no bets then the fallback offer will be used
    if (cards.value?.length === 0) {
      return false;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return false;
    }

    // Has an offer been defiend for this opertor
    if (
      adTag &&
      adTag.selectedOperator.value &&
      brand?.operatorHasOffer(adTag.selectedOperator.value?._id || "")
    ) {
      // Are offers enabled for this brand
      if (displayOfferCard.value) {
        return true;
      }
    }
    return false;
  });

  return {
    offerDisplayedAsFallback,
    defaultOfferDisplayedAsFirstCard
  };
}

export function useGetOffer(): ComputedRef<Offer | undefined> {
  const betData = injectBetData();
  const brand = injectBrand();
  const adTag = injectAdTag();
  const previewContext = injectPreview();

  const brandOffer = computed(() => {
    const operatorOffer =
      adTag &&
      brand &&
      adTag.selectedOperator.value &&
      brand.operatorHasOffer(adTag.selectedOperator.value?._id || "") &&
      brand.offers.value?.[adTag.selectedOperator.value?._id || ""];

    if (operatorOffer) {
      return operatorOffer;
    }

    return undefined;
  });

  const displayOffer = computed(() => {
    // Override for previewing the offer for an operator on brands page.
    if (
      adTag &&
      previewContext?.preview.value.useBrandOffer &&
      brand?.offers.value?.[adTag.selectedOperator.value?._id || ""]
    ) {
      return brandOffer.value;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      // use the link from the brand if there isn't one set on the campaign/experiment
      if (
        !betData.offer.value?.link &&
        betData.offer.value?.link === "" &&
        brandOffer?.value?.link
      ) {
        betData.offer.value.link = brandOffer.value.link;
      }

      if (betData.offer.value?.link === "") {
        const { fallbackUrl } =
          adTag?.adTag.value?.tracking[
            adTag.selectedOperator.value?._id || ""
          ] || {};
        if (fallbackUrl) {
          betData.offer.value.link = fallbackUrl;
        }
      }

      // use the terms from the brand if there isn't one set on the campaign/experiment
      if (
        !betData.offer.value?.terms &&
        betData.offer.value?.terms === "" &&
        brandOffer?.value?.terms
      ) {
        betData.offer.value.terms = brandOffer.value.terms || "";
      }

      return (
        betData.offer.value &&
        generateLinks.offer(betData.offer.value, {
          affiliateId: betData?.affiliateId.value,
          clickUrl: betData?.clickUrl.value || "",
          operatorKey: adTag?.selectedOperator.value?.key || ""
        })
      );
      // Check to see if we can use the brand based offer for the opertor
    } else {
      const offer: Offer = {
        header: brandOffer.value?.header || "",
        body: brandOffer.value?.body || "",
        buttonText: brandOffer.value?.buttonText || "",
        link: brandOffer.value?.link || "",
        terms: brandOffer.value?.terms || ""
      };
      const targetUrl = adTag?.selectedCampaign.value?.targetUrl;
      const { fallbackUrl } =
        adTag?.adTag.value?.tracking[adTag.selectedOperator.value?._id || ""] ||
        {};
      if (targetUrl && targetUrl !== "") {
        offer.link = targetUrl;
      } else if (fallbackUrl && fallbackUrl !== "") {
        offer.link = fallbackUrl;
      }
      return generateLinks.offer(offer, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.clickUrl.value || "",
        operatorKey: adTag?.selectedOperator.value?.key || ""
      });
    }
  });

  return displayOffer;
}
